<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import stores from '@ganaderia/stores';
import useSync from '@ganaderia/composables/useSync';
import { db } from '@ganaderia/composables/useDexie';
import Snackbar from '@shared/components/Snackbar.vue';
import NavigationDrawer from '@ganaderia/components/NavigationDrawer.vue';
import Footer from '@ganaderia/components/Footer.vue';

const route = useRoute();
const sync = useSync();

const pleaseSyncDialogIsOpen = ref(false);
const overdueMessageClosed = ref(false);
const syncingData = ref(false);

const farmColorIndex = computed(() => {
  if (!stores.auth.state.account.value) return -1;
  return stores.auth.state.account.value.farms.map((f) => f.id).indexOf(stores.app.state.selectedFarmId.value);
});

const spinnerColor = computed(() => (stores.auth.state.account.value ? `color-${farmColorIndex.value}` : 'white'));

const colorNavbar = computed(() => {
  if (['cows', 'cow-details'].includes(route.name as string)) {
    return stores.auth.state.account.value ? `color-${farmColorIndex.value}` : undefined;
  }
  return undefined;
});

const accountOverdue = computed(
  () => !!stores.auth.state.account.value && new Date(stores.auth.state.account.value.valid_until) < new Date()
);

const updateOnlineStatusAndSyncs = async () => {
  stores.offline.state.online.value = navigator.onLine;
  if (navigator.onLine && !syncingData.value && !stores.offline.state.userDeniedSync.value) {
    const count = await db.syncs.count();
    if (count > 0) pleaseSyncDialogIsOpen.value = true;
  }
  setTimeout(() => sync.updateSyncLength(), 1500);
};

const saveUserDeniedSync = () => {
  stores.offline.state.userDeniedSync.value = true;
  pleaseSyncDialogIsOpen.value = false;
};

const runSync = async () => {
  syncingData.value = true;
  pleaseSyncDialogIsOpen.value = false;
  await sync.syncData();
  syncingData.value = false;
};

onMounted(() => {
  if (stores.auth.state.user.value) {
    stores.cows.funcs.fetchAllCowsUpdateCache();
  }
  updateOnlineStatusAndSyncs();
  document.addEventListener('click', updateOnlineStatusAndSyncs);
  window.addEventListener('online', updateOnlineStatusAndSyncs);
  window.addEventListener('offline', updateOnlineStatusAndSyncs);
});
onUnmounted(() => {
  document.removeEventListener('click', updateOnlineStatusAndSyncs);
  window.removeEventListener('online', updateOnlineStatusAndSyncs);
  window.removeEventListener('offline', updateOnlineStatusAndSyncs);
});
</script>

<template>
  <v-app>
    <v-overlay :model-value="stores.app.state.loading.value" contained class="align-center justify-center" scrim="#000" :opacity="0.6" style="position: fixed" :z-index="1000">
      <div class="loading-spinner text-center">
        <v-progress-circular size="80" width="10" indeterminate :color="spinnerColor"></v-progress-circular>
        <div class="mt-2 text-white">{{ stores.app.state.loadingMessage.value }}</div>
      </div>
    </v-overlay>

    <v-navigation-drawer v-model="stores.app.state.drawerOpen.value" location="right" temporary class="z-30">
      <NavigationDrawer />
    </v-navigation-drawer>

    <v-app-bar :color="colorNavbar" :class="{ transparent: !stores.app.state.showAppbar.value }">
      <div class="d-flex w-100 justify-space-between align-center px-2">
        <div v-if="!stores.app.state.showAppbar.value"></div>
        <v-btn v-if="stores.app.state.showAppbar.value" :to="stores.app.state.back.value" variant="text" icon>
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div v-if="stores.app.state.showAppbar.value">{{ stores.app.state.navbarText.value }}</div>
        <div>
          <v-icon v-if="!stores.offline.state.online.value">mdi-wifi-off</v-icon>
          <v-app-bar-nav-icon
            :class="{ 'bg-white': !stores.app.state.showAppbar.value }"
            @click.stop="stores.app.state.drawerOpen.value = !stores.app.state.drawerOpen.value"
          ></v-app-bar-nav-icon>
        </div>
      </div>
    </v-app-bar>

    <v-alert color="warning" class="mb-0 w-100 text-center" v-if="!overdueMessageClosed && accountOverdue">
      Your account is not valid anymore. A new payment is requiered. Open the settings to make a new transaction.
      <v-btn size="small" class="ml-4" @click="overdueMessageClosed = true">Ok</v-btn>
    </v-alert>

    <v-main :class="{ 'max-width mx-auto pa-5 mt-main w-100': route.name !== 'home' }">
      <router-view :key="route.fullPath"></router-view>
    </v-main>

    <Footer />

    <v-dialog v-model="pleaseSyncDialogIsOpen" width="400px">
      <v-card class="pa-5">
        <div>Tienes datas offline que no estan syncronizado. Por favor syncronizar los datos para prohibir errores</div>
        <div class="mt-4">
          <v-btn size="small" color="secondary" class="mr-4" @click="saveUserDeniedSync">Cancelar</v-btn>
          <v-btn size="small" color="primary" class="text-white" @click="runSync">Syncronizar</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <Snackbar />
  </v-app>
</template>
